import React, { useEffect, useMemo, useRef, useState } from 'react';

import PackageTab from './PackageTab';
import OmnitureUtils from '../../../../../../common/utils/Omniture/OmnitureUtils';
import {
  faOnCollectionsTabSelect,
  faOnLeftArrowClick,
  faOnRightArrowClick,
} from './analytics-services';
import './styles.scss';

const PackageTabSection = props => {
  const { tabs, activeTab, handleTabSelect, isLoading, groupPackagesMap } = props;

  const [showArrows, setShowArrows] = useState(false);
  const [disabledState, setDisabledState] = useState({ left: true, right: false });

  const tabContainerRef = useRef(null);
  const tabScrollRef = useRef(null);

  // Update arrow disabled states based on scroll position
  const updateArrowStates = () => {
    const container = tabScrollRef.current;
    const tabsRect = tabContainerRef.current?.getBoundingClientRect();
    const containerRect = container?.getBoundingClientRect();

    if (!container || !tabsRect || !containerRect) return;

    const currentScrollLeft = container.scrollLeft || 0;
    const maxScroll = tabsRect.width - containerRect.width;

    setDisabledState({
      left: currentScrollLeft <= 10,
      right: currentScrollLeft >= maxScroll - 10,
    });
  };

  useEffect(() => {
    if (tabs?.length) {
      const tabsRect = tabContainerRef.current.getBoundingClientRect();
      const containerRect = tabScrollRef.current.getBoundingClientRect();
      if (tabsRect?.width > containerRect?.width || 0 - 50) {
        setShowArrows(true);
      }
    }
  }, [tabs]);

  // Add scroll event listener to update arrow states on manual scroll
  useEffect(() => {
    const container = tabScrollRef.current;
    if (!container) return;

    container.addEventListener('scroll', updateArrowStates);

    return () => {
      container.removeEventListener('scroll', updateArrowStates);
    };
  }, []);

  const handleRightArrowClick = () => {
    OmnitureUtils.trackClickEvent({ prop54: 'Collection_click_right' });
    faOnRightArrowClick();
    const container = tabScrollRef.current;
    const tabsRect = tabContainerRef.current?.getBoundingClientRect();
    const containerRect = container?.getBoundingClientRect();
    const delta = Math.ceil((tabsRect?.width || 0) / (containerRect?.width || 1)) + 1;
    const deltaWidth = Math.floor(tabsRect.width / delta) + 10;

    const currentScrollLeft = container?.scrollLeft || 0;
    const newScrollLeft = currentScrollLeft + deltaWidth;
    const maxScroll = tabsRect.width - containerRect.width;

    // Update disabled state in a single call
    setDisabledState({
      left: false, // Can now scroll left
      right: newScrollLeft >= maxScroll - 10, // Disable right if near the end (10px threshold)
    });

    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    });
  };

  const handleLeftArrowClick = () => {
    OmnitureUtils.trackClickEvent({ prop54: 'Collection_click_left' });
    faOnLeftArrowClick();
    const container = tabScrollRef.current;
    const tabsRect = tabContainerRef.current?.getBoundingClientRect();
    const containerRect = container?.getBoundingClientRect();
    const delta = Math.ceil((tabsRect?.width || 0) / (containerRect?.width || 1)) + 1;
    const deltaWidth = Math.floor(tabsRect.width / delta) + 10;

    const currentScrollLeft = container?.scrollLeft || 0;
    const newScrollLeft = Math.max(0, currentScrollLeft - deltaWidth);

    // Update disabled state in a single call
    setDisabledState({
      left: newScrollLeft <= 10, // Disable left if at or near the start (10px threshold)
      right: false, // Can now scroll right
    });

    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    });
  };

  const onTabSelect = (newActiveTab, index) => {
    faOnCollectionsTabSelect(`Collection_${index}_${newActiveTab?.shortName}`);
    handleTabSelect(newActiveTab, index);
  };
  const listingPackagesCount = useMemo(
    () => groupPackagesMap?.listing?.numFound || '',
    [groupPackagesMap]
  );

  return (
    <div className="tabStickyWrapper">
      <div className="tabScrollSection" id="collectionList" ref={tabScrollRef}>
        {showArrows && (
          <span className={`srollTapLeft ${disabledState?.left ? 'disabled' : ''}`}>
            <span className="icon mmt-chevron-left" onClick={handleLeftArrowClick} />
          </span>
        )}
        <ul ref={tabContainerRef} className="tabsWrapper">
          {isLoading && !tabs?.length ? (
            <>
              {[...new Array(5)].map((_, i) => (
                <li key={i} className="tabLoader" />
              ))}
            </>
          ) : tabs.length ? (
            <>
              {tabs.map((item, index) => (
                <PackageTab
                  index={index}
                  item={item}
                  key={item.shortName}
                  handleTabSelect={onTabSelect}
                  isActive={activeTab?.id === item.id}
                />
              ))}
            </>
          ) : (
            <li className="active">
              <span className="name">
                All Packages {listingPackagesCount ? `(${listingPackagesCount})` : ''}
              </span>
            </li>
          )}
        </ul>
        {showArrows && (
          <span className={`srollTapRight ${disabledState?.right ? 'disabled' : ''}`}>
            <span className="icon mmt-chevron-right" onClick={handleRightArrowClick} />
          </span>
        )}
      </div>
    </div>
  );
};

export default PackageTabSection;
